import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ExceptionTrackingService } from '@com-delta-idp/mnx-utils';
import {
  EXCEPTION_CATEGORY_ENUM,
  ErrorResponse,
  IExceptionMetadata,
  LoginOAuth,
  LoginOAuthParams
} from '../../models';
import { LoginAuthUrl } from '../../models/util.interface';
import { UtilMethodsService } from '../util-service/util-methods.service';
import { UtilService } from '../util-service/util-service';
const fileName = 'AuthorizationService';
@Injectable({
  providedIn: 'root'
})
export class AuthorizationService {
  constructor(
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) private platformId: Object,
    private http: HttpClient,
    private utils: UtilService,
    private utilMethodService: UtilMethodsService,
    private exceptionTracking: ExceptionTrackingService
  ) {}

  async loginOAuth(
    loginOAuthParams: LoginOAuthParams
  ): Promise<LoginOAuth | ErrorResponse> {
    try {
      let loginOAuthUrl = '';
      if (
        loginOAuthParams?.oauthConfig &&
        loginOAuthParams?.pkceCode?.codeChallenge
      ) {
        loginOAuthUrl = this.getLoginAuthUrl(loginOAuthParams);
      } else {
        const errorResponse = {
          errors: [{ code: 'ERROR_MSG_200' }]
        };
        return errorResponse;
      }
      return await this.getLoginOauthData(loginOAuthParams, loginOAuthUrl);
    } catch (exception) {
      const exceptionMetadata: IExceptionMetadata = {
        fileName,
        methodName: 'loginOAuth',
        exceptionCategory: EXCEPTION_CATEGORY_ENUM.JS_CATCH_BLOCK,
        exceptionMessage: 'Exception caught while calling authorization service'
      };
      const exceptionStr = exception as string;
      this.exceptionTracking.track(exceptionStr, exceptionMetadata);
      return exception as LoginOAuth;
    }
  }

  getLoginAuthUrl(loginOAuthParams: LoginOAuthParams): string {
    const pingServiceUrl: LoginAuthUrl = this.utils.getPingServiceUrl(
      loginOAuthParams.oauthConfig,
      undefined,
      loginOAuthParams.pkceCode.codeChallenge
    );
    if (
      loginOAuthParams?.persistentCookie &&
      !loginOAuthParams.oauthConfig?.REMEMBERME_API_FLOW_ENABLE
    ) {
      return this.getUrlBasedOnPromptValue(loginOAuthParams, pingServiceUrl);
    } else {
      if (!loginOAuthParams.oauthConfig?.REMEMBERME_API_FLOW_ENABLE) {
        return pingServiceUrl.loginOAuthUrl;
      }
      return this.getUrlBasedOnStorageValue(loginOAuthParams, pingServiceUrl);
    }
  }

  getUrlBasedOnPromptValue(
    loginOAuthParams: LoginOAuthParams,
    pingServiceUrl: LoginAuthUrl
  ): string {
    if (loginOAuthParams.prompt) {
      return pingServiceUrl.loginOAuthUrlPersistant;
    }
    return pingServiceUrl.loginOAuthUrl;
  }

  getUrlBasedOnStorageValue(
    loginOAuthParams: LoginOAuthParams,
    pingServiceUrl: LoginAuthUrl
  ): string {
    const encryptedTokenStored = this.utils.getLocalStorageValue('encyToken');
    const uniqueIdentifierStored = this.utils.getLocalStorageValue('uid');
    if (encryptedTokenStored && uniqueIdentifierStored) {
      if (loginOAuthParams.prompt) {
        return pingServiceUrl.loginOAuthUrlPersistantRememberMe;
      } else {
        return pingServiceUrl.loginOAuthUrlRememberMe;
      }
    }
    return pingServiceUrl.loginOAuthUrlRememberMe;
  }

  async getLoginOauthData(
    loginOAuthParams: LoginOAuthParams,
    loginOAuthUrl: string
  ): Promise<LoginOAuth | ErrorResponse> {
    if (isPlatformBrowser(this.platformId) && loginOAuthUrl) {
      const headers: HttpHeaders = {} as HttpHeaders;
      const data: LoginOAuth = await this.http
        .get<LoginOAuth>(`${loginOAuthUrl}`, {
          headers: headers,
          responseType: 'json',
          withCredentials: true
        })
        .toPromise()
        .catch((error) => {
          if (
            this.utilMethodService.checkErrorMessage(loginOAuthParams, error)
          ) {
            const errorResponse = {
              errors: [{ code: 'ERROR_MSG_128' }]
            };
            return errorResponse;
          }
          return error;
        });
      return data;
    } else {
      const errorResponse = {
        errors: [{ code: 'ERROR_MSG_99' }]
      };
      return errorResponse;
    }
  }
}
